import {
  parseRpcParams,
  parseRpcResult,
  type RpcMethod,
  type RpcParams,
  type RpcResult,
} from "@lodestone/hungry-lion-procurement-contracts";
import {
  JSONRPCClient,
  JSONRPCErrorException,
  type JSONRPCResponse,
} from "json-rpc-2.0";

export type AuthenticatedActor = {
  actorId: string;
  displayName: string;
};

export type ExternalTenantContext = {
  organizationId: string;
  displayName: string;
};

export type AuthSession = {
  signIn(returnTo: string): Promise<void>;
  signOut(returnTo: string): Promise<void>;
  getActor(): Promise<AuthenticatedActor | undefined>;
  listTenantContexts(): Promise<ExternalTenantContext[]>;
  selectTenant(organizationId: string): Promise<void>;
  getApiAccessToken(): Promise<string>;
};

export class RpcError extends Error {
  constructor(
    readonly code: number,
    message: string,
    readonly data?: unknown,
  ) {
    super(message);
    this.name = "RpcError";
  }
}

type RpcClientOptions = {
  authSession: Pick<AuthSession, "getApiAccessToken">;
  fetch?: (request: Request) => Promise<Response>;
  id?: () => string;
  endpoint?: string;
  timeoutMs?: number;
};

export type RpcClient = {
  call<M extends RpcMethod>(
    method: M,
    params: RpcParams<M>,
  ): Promise<RpcResult<M>>;
};

export const createRpcClient = (options: RpcClientOptions): RpcClient => {
  const fetcher = options.fetch ?? ((request: Request) => fetch(request));
  const nextId = options.id ?? (() => crypto.randomUUID());
  const endpoint =
    options.endpoint ??
    (typeof location === "undefined"
      ? "http://localhost/rpc"
      : new URL("/rpc", location.origin).toString());
  const timeoutMs = options.timeoutMs ?? 15_000;
  const protocol = new JSONRPCClient(async (payload) => {
    const request = payload as { id?: unknown };
    const token = await options.authSession.getApiAccessToken();
    const response = await fetcher(
      new Request(endpoint, {
        method: "POST",
        headers: {
          authorization: `Bearer ${token}`,
          "content-type": "application/json",
        },
        body: JSON.stringify(payload),
        signal: AbortSignal.timeout(timeoutMs),
      }),
    );
    const envelope = (await response.json()) as JSONRPCResponse;
    if (envelope.id !== request.id) {
      throw new Error("JSON-RPC response ID mismatch");
    }
    protocol.receive(envelope);
  }, nextId);
  return {
    async call(method, params) {
      try {
        const validatedParams = parseRpcParams(method, params);
        const result: unknown = await protocol.request(
          method,
          validatedParams,
          undefined,
        );
        return parseRpcResult(method, result);
      } catch (error) {
        if (error instanceof JSONRPCErrorException) {
          throw new RpcError(error.code, error.message, error.data);
        }
        throw error;
      }
    },
  };
};

type WebSocketLike = {
  addEventListener(
    type: "open" | "close" | "message",
    listener: (event: Event | MessageEvent) => void,
  ): void;
  close(): void;
  send(message: string): void;
};

type RealtimeOptions = {
  rpc: RpcClient;
  url: string;
  webSocket?: (url: string, protocols: string[]) => WebSocketLike;
  onInvalidated(notification: {
    type: "procurement.changed";
    tenantId: string;
    projectId: string;
    aggregateId: string;
    aggregateVersion: number;
  }): void;
  onResync(): void;
  reconnectDelayMs?: (attempt: number) => number;
};

export const createRealtimeClient = (options: RealtimeOptions) => {
  const createSocket =
    options.webSocket ??
    ((url: string, protocols: string[]): WebSocketLike =>
      new WebSocket(url, protocols));
  const reconnectDelay =
    options.reconnectDelayMs ??
    ((attempt: number) =>
      Math.min(30_000, 500 * 2 ** attempt) * (0.75 + Math.random() * 0.5));
  let socket: WebSocketLike | undefined;
  let stopped = true;
  let attempt = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  let reauthorizeTimer: ReturnType<typeof setTimeout> | undefined;
  let openTimer: ReturnType<typeof setTimeout> | undefined;

  const issueGrant = () => options.rpc.call("realtime.issueGrant", {});

  const scheduleReauthorization = (expiresAt: string) => {
    if (reauthorizeTimer) clearTimeout(reauthorizeTimer);
    const delay = Math.max(0, Date.parse(expiresAt) - Date.now() - 15_000);
    reauthorizeTimer = setTimeout(() => {
      void issueGrant()
        .then((result) => {
          socket?.send(
            JSON.stringify({ type: "reauthorize", grant: result.grant }),
          );
          scheduleReauthorization(result.expiresAt);
        })
        .catch(() => socket?.close());
    }, delay);
  };

  const scheduleReconnect = () => {
    if (stopped || reconnectTimer) return;
    const delay = reconnectDelay(attempt++);
    reconnectTimer = setTimeout(() => {
      reconnectTimer = undefined;
      void connect();
    }, delay);
  };

  const connect = async () => {
    let grant: Awaited<ReturnType<typeof issueGrant>>;
    try {
      grant = await issueGrant();
    } catch {
      scheduleReconnect();
      return;
    }
    if (stopped) return;
    socket = createSocket(options.url, ["lodestone.jsonrpc.v1"]);
    openTimer = setTimeout(() => socket?.close(), 10_000);
    socket.addEventListener("open", () => {
      if (openTimer) clearTimeout(openTimer);
      attempt = 0;
      scheduleReauthorization(grant.expiresAt);
      socket?.send(
        JSON.stringify({
          jsonrpc: "2.0",
          id: crypto.randomUUID(),
          method: "realtime.subscribe",
          params: {
            projectId: new URL(options.url).searchParams.get("projectId"),
          },
        }),
      );
      options.onResync();
    });
    socket.addEventListener("message", (event) => {
      if (!(event instanceof MessageEvent) || typeof event.data !== "string")
        return;
      const value = JSON.parse(event.data) as {
        jsonrpc?: unknown;
        method?: unknown;
        params?: {
          type?: unknown;
          tenantId?: unknown;
          projectId?: unknown;
          aggregateId?: unknown;
          aggregateVersion?: unknown;
        };
      };
      const notification = value.params;
      if (
        value.jsonrpc === "2.0" &&
        value.method === "procurement.changed" &&
        notification?.type === "procurement.changed" &&
        typeof notification.tenantId === "string" &&
        typeof notification.projectId === "string" &&
        typeof notification.aggregateId === "string" &&
        typeof notification.aggregateVersion === "number"
      ) {
        options.onInvalidated({
          type: notification.type,
          tenantId: notification.tenantId,
          projectId: notification.projectId,
          aggregateId: notification.aggregateId,
          aggregateVersion: notification.aggregateVersion,
        });
      }
    });
    socket.addEventListener("close", () => {
      if (openTimer) clearTimeout(openTimer);
      if (stopped) return;
      scheduleReconnect();
    });
  };

  return {
    start() {
      if (!stopped) return;
      stopped = false;
      void connect();
    },
    stop() {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (reauthorizeTimer) clearTimeout(reauthorizeTimer);
      if (openTimer) clearTimeout(openTimer);
      socket?.close();
      socket = undefined;
    },
  };
};
